import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import { TableHeadComponent } from "../../common/TableHead";
import { createCameraAlarmType } from "./EditCameraUtils/createCameraAlarmType";
import { getNumbersAfterEditCamera } from "./EditCamera";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import { CameraMotionSetting } from "./CameraMotionSetting";
import { checkedCircleIcon } from "../../common/checkedCircleIcon";
import { cancelIconRed } from "../../common/cancelIconRed";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useAppStore } from "../../../appStore";
import {
  GENERAL_INFO_COLUMN_COUNT,
  generalInfoTableCellSx,
} from "./EditCameraUtils/editCameraGeneralInfoLayout";
import {
  GeneralInfoConnectionHeaderRows,
  GeneralInfoConnectionViewRows,
} from "./GeneralInfoConnectionTableRows";

export const GeneralInfoEditCameraTab = ({
  siteInfo,
  cameraGeneralInfo,
}: {
  siteInfo: any;
  cameraGeneralInfo: any;
}) => {
  const { userPermissions } = useAppStore();
  const cameraViewOptions = JSON.parse(localStorage.cameraViewOptions);

  const cameraViewOptionNames = new Map();
  cameraViewOptions.forEach((option: any) => {
    cameraViewOptionNames.set(option.value, option.name);
  });

  const connectionsTitlesArray = JSON.parse(
    localStorage.connectionsTitlesArray
  );

  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const [titlesArray, setTitlesArray] = React.useState(["", ""]);

  const { chmActive, setNotesDialogEditCameraOpen } = useEditCameraStore();

  const autopatrolSite = siteInfo.integration_type_name === "autopatrol";

  React.useEffect(() => {
    if (cameraGeneralInfo.streams[0].show_nvr_alarm) {
      setTitlesArray(["SES recipients", "NVR Alarm"]);
    } else {
      setTitlesArray(["SES recipients", ""]);
    }
  }, []);

  const generalInfoHeaders = autopatrolSite
    ? ["Customer", "Camera Name", "Active", "Notes"]
    : [
        "Customer",
        "Camera Name",
        "Camera View",
        "Alarm Type",
        "Active",
        "Camera Health Monitoring",
        "Notes",
      ];

  return (
    <TableContainer component={Paper}>
      <Table
        sx={{ minWidth: 1000, fontFamily: "Mulish", tableLayout: "fixed" }}
        aria-label="simple table"
      >
        <colgroup>
          {Array.from({ length: GENERAL_INFO_COLUMN_COUNT }).map((_, index) => (
            <col
              key={`general-info-col-${index}`}
              style={{ width: `${100 / GENERAL_INFO_COLUMN_COUNT}%` }}
            />
          ))}
        </colgroup>
        <TableHeadComponent titlesArray={generalInfoHeaders} />
        <TableBody>
          <TableRow key={getNumbersAfterEditCamera(location.href)}>
            <TableCell align="left" sx={generalInfoTableCellSx}>
              {siteInfo.parent_group.name}
            </TableCell>
            <TableCell align="left" sx={generalInfoTableCellSx}>
              {cameraGeneralInfo.camera_name}
            </TableCell>
            {!autopatrolSite && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                {cameraViewOptionNames.get(cameraGeneralInfo.camera_view)}
              </TableCell>
            )}
            {!autopatrolSite && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                {createCameraAlarmType(cameraGeneralInfo.default_alarm)}
              </TableCell>
            )}
            <TableCell align="left" sx={generalInfoTableCellSx}>
              <Switch
                checked={cameraGeneralInfo.active}
                color="warning"
                disabled={true}
              />
            </TableCell>
            {!autopatrolSite && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                {chmActive ? checkedCircleIcon : cancelIconRed}
              </TableCell>
            )}
            <TableCell align="left" sx={generalInfoTableCellSx}>
              <OrangeTextButton
                onClick={() => {
                  setNotesDialogEditCameraOpen(true);
                }}
                text="See Notes"
                disabled={false}
              />
            </TableCell>
          </TableRow>
          {!autopatrolSite && (
            <>
              <GeneralInfoConnectionHeaderRows
                connectionTitles={connectionsTitlesArray}
              />
              <GeneralInfoConnectionViewRows
                siteInfo={siteInfo}
                cameraGeneralInfo={cameraGeneralInfo}
                userPermissions={userPermissions}
                showPassword={showPassword}
                onToggleShowPassword={handleClickShowPassword}
                onMouseDownPassword={handleMouseDownPassword}
                onMouseUpPassword={handleMouseUpPassword}
              />
            </>
          )}
        </TableBody>
      </Table>
      {!autopatrolSite && (
        <Table
          sx={{ minWidth: 1000, fontFamily: "Mulish" }}
          aria-label="simple table"
        >
          <TableHeadComponent titlesArray={titlesArray} />
          <TableBody>
            <TableRow key={getNumbersAfterEditCamera(location.href)}>
              <TableCell align="left" sx={{ fontFamily: "Mulish", width: 600 }}>
                {cameraGeneralInfo.streams[0].ses_recipients
                  ? cameraGeneralInfo.streams[0].ses_recipients
                  : "--"}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {cameraGeneralInfo.streams[0].show_nvr_alarm && (
                  <Switch
                    checked={cameraGeneralInfo.streams[0].nvr_alarm}
                    color="warning"
                    disabled={true}
                  />
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      <CameraMotionSetting
        cameraGeneralInfo={cameraGeneralInfo}
        siteInfo={siteInfo}
      />
    </TableContainer>
  );
};
